import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { LANGUAGES, getLanguageMeta } from '../../i18n/languages';
import './LanguageSwitcher.css';

/**
 * Language switcher (EN / 中文).
 *
 * Phase A note: this switches the active language via i18next and persists the
 * choice. Once the /zh URL routes are mounted, the click handler should also
 * navigate to the prefixed/unprefixed URL so the language and the URL stay in
 * sync (see docs/i18n-implementation-plan.md, section 4/6).
 *
 * @param {{ variant?: 'header' | 'footer' }} props
 */
const LanguageSwitcher = ({ variant = 'header' }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    // The URL prefix is the source of truth for the active language.
    const isZh =
        location.pathname === '/zh' || location.pathname.startsWith('/zh/');
    const active = isZh ? 'zh' : 'en';

    const changeTo = (code) => {
        if (code === active) return;
        // Strip any existing /en or /zh prefix to get the language-neutral path.
        const p = location.pathname;
        const basePath =
            (p === '/zh' || p === '/en'
                ? '/'
                : p.startsWith('/zh/') || p.startsWith('/en/')
                ? p.slice(3)
                : p) || '/';
        const prefix = code === 'zh' ? '/zh' : '/en';
        const target = basePath === '/' ? prefix : `${prefix}${basePath}`;
        try {
            localStorage.setItem('i18nextLng', code);
        } catch (e) {
            /* storage may be unavailable (private mode) — ignore */
        }
        navigate(`${target}${location.search}${location.hash}`);
    };

    return (
        <div
            className={`lang-switcher lang-switcher--${variant}`}
            role="group"
            aria-label={t('common.languageSwitcher.label')}
        >
            {LANGUAGES.map((l, idx) => (
                <React.Fragment key={l.code}>
                    {idx > 0 && (
                        <span className="lang-switcher__sep" aria-hidden="true">
                            |
                        </span>
                    )}
                    <button
                        type="button"
                        lang={l.htmlLang}
                        className={`lang-switcher__btn${
                            l.code === active ? ' is-active' : ''
                        }`}
                        aria-pressed={l.code === active}
                        onClick={() => changeTo(l.code)}
                    >
                        {l.short}
                    </button>
                </React.Fragment>
            ))}
        </div>
    );
};

export default LanguageSwitcher;
